import axios, { AxiosResponse } from 'axios';
import { IDefaults, IGeoData, IStorage } from 'models';
import { API_GEO, CONNECT_ENDPOINT, DEFAULT_WID, VERSION_CHECK } from 'util/constants';
import * as helpers from 'util/helpers';

// Vote API Action Types
export enum VOTE_API_ACTION_TYPE {
  GET = 'get',
  LOGIN = 'login',
  HEARTBEAT = 'active_session',
  LOGOUT = 'logout',
  GET_SURVEY = 'get_survey',
  SUBMIT_SURVEY = 'submit_survey',
  SUBMIT_STAR = 'submit_star',
}

// Response Codes
export enum VOTE_API_RESPONSE_CODE {
  GENERAL_INVALID = '16',
  DUPLICATE_LOGIN = '17',
  OUT_OF_WINDOW = '40',
  OVERLIMIT = '21',
  OUT_OF_GEO = '22',
  VALID = '20',
}

export interface VoteApiResponse {
  response_code: string;
  stars: string;
  survey: string;
  personal_data: string;
  session: string;
  survey_completed: string;
}

class Api {
  private storage = this.initializeStorage();
  private defaults: IDefaults = { wid: DEFAULT_WID };

  constructor() {
    this.getQSPs();
  }

  public fetchGeo(): Promise<IGeoData> {
    return axios
      .get(API_GEO)
      .then((res: AxiosResponse) => res.data)
      .catch((err) => {
        throw new Error(err);
      });
  }

  public fetchAbstract(url: string): Promise<any> {
    return axios
      .get(url)
      .then((response) => {
        return response.data;
      })
      .catch((error) => {
        throw new Error(error);
      });
  }

  public fetchBios(urls: string[]): Promise<any> {
    const requests = urls.map((url) => {
      return axios.get(url);
    });

    return axios
      .all(requests)
      .then((responseArray) => {
        return responseArray.map((res) => res.data);
      })
      .catch((err) => {
        throw new Error(err);
      });
  }

  public updateActiveSession(apiKey: string, version_id: string, code: string, token: string): any {
    const payload = {
      apiKey,
      timestamp: Date.now(),
      code,
      token,
      action_type: VOTE_API_ACTION_TYPE.HEARTBEAT,
    };

    return this.postToVoteApi(payload, null, version_id);
  }

  /**
   * Sorts a javascript object alphabetically by key
   * @param unsortedObj - a javascript object to sort
   * @returns sorted javascript object by key
   */
  private sortObjectByKey(unsortedObj: any): any {
    return Object.keys(unsortedObj)
      .sort()
      .reduce((obj: any, key: string) => {
        obj[key] = unsortedObj[key];
        return obj;
      }, {});
  }

  public postToVoteApi(payload: any, body: any, versionId: string): Promise<VoteApiResponse> {
    const wid = (window as any).WID || this.defaults.wid;
    let formData = new FormData();
    payload.wid = wid;
    const qsp = helpers.convertToQspString(payload);

    let combinedParams = qsp;
    if (body) {
      const bodySorted = this.sortObjectByKey({ ...body });
      const bodyParams = helpers.convertToQspString(bodySorted, false);
      formData = this.createFormData(bodySorted, false);
      combinedParams += '&' + bodyParams;
    }

    const hash = helpers.createHash(combinedParams, `${versionId}${VERSION_CHECK}`);

    const url = `${CONNECT_ENDPOINT}?${qsp}`;
    const options = {
      headers: {
        Authorization: hash,
      },
    };

    return axios
      .post(url, formData, options)
      .then((res: AxiosResponse) => res.data)
      .catch((err) => console.error('Voteapi call failed:', err.message));
  }

  /**
   * converts a javascript object into a Form data
   * @param postBodyObj - javascript object
   * @returns FormData
   */
  private createFormData(postBodyObj: any, encodeValue = true): FormData {
    const formData = new FormData();
    for (const key in postBodyObj) {
      if (postBodyObj.hasOwnProperty(key)) {
        const value = postBodyObj[key];
        if (value) {
          const encodedValue = helpers.fixedEncodeUriComponent(String(value));
          formData.append(key, encodeValue ? encodedValue : String(value));
        }
      }
    }
    return formData;
  }

  private getQSPs(): void {
    window.location.search
      .slice(1)
      .split('&')
      .forEach((item) => {
        const [key, value = ''] = item.split('=');

        this.storage.qsps[key] = value;
      });
  }

  /**
   * initializeStorage
   */
  private initializeStorage(): IStorage {
    const storage = Object.create(null);
    storage.cms = Object.create(null);
    storage.qsps = Object.create(null);

    return storage;
  }
}

export default new Api();
